import { FocusTrap } from 'focus-trap-react';
import { Link } from 'gatsby';
import React, { useEffect, useState } from 'react';

type LayoutProps = {
  children: React.ReactNode;
  data?: Record<string, any>;
};

const paths = Object.entries({ Home: '/', Projects: '/projects' });
const thisYear = new Date().getFullYear();

const NavigationLink: React.FC<{ label: string; path: string }> = ({ label, path }) => (
  <Link
    to={path}
    className="px-3 no-underline lg:px-2.5"
    activeClassName="border-s-6 ps-1.5 lg:border-s-0 lg:p-2.5 lg:border-b-2"
  >
    {label}
  </Link>
);

const findStringValueFromData = (
  data?: Record<string, Record<string, unknown>>,
  prop = 'title',
): string | null => {
  if (!data) return null;
  for (const node of Object.values(data)) {
    const val = node?.[prop];
    if (typeof val === 'string') {
      const s = val.trim();
      if (s) return s;
    }
  }
  return null;
};

const Layout = ({ children, data }: LayoutProps) => {
  const title = findStringValueFromData(data, 'title');
  const mainText = findStringValueFromData(data, 'main_text');
  const subtitle = findStringValueFromData(data, 'subtitle');
  const [showSidebar, setShowSidebar] = useState(false);
  useEffect(() => {
    const className = 'overflow-hidden';
    if (showSidebar) {
      document.body.classList.add(className);
      return () => document.body.classList.remove(className);
    } else {
      document.body.classList.remove(className);
    }
  }, [showSidebar]);
  return (
    <div className="relative min-h-screen" id="#true">
      <aside
        role="dialog"
        className="sticky top-0 z-1 bg-white pb-2.5"
        aria-modal="true"
      >
        <div className={`mb-1.25 grid grid-cols-3 ${showSidebar ? '' : 'lg:hidden'}`}>
          {showSidebar ? (
            <FocusTrap>
              <nav
                onClick={() => setShowSidebar(false)}
                className="fixed top-0 z-1 h-full min-w-full overflow-auto bg-white/80 pb-8"
              >
                <button
                  className="ms-5 mt-4.75 px-1.5 py-0.75 focus:outline-0"
                  aria-label="Hide menu"
                  type="button"
                  aria-expanded={showSidebar}
                >
                  <img src="/images/menu.svg" alt="Menu" width={50} height={50} />
                </button>
                <ul className="ms-6 mt-3 grid grid-cols-1 gap-y-3 text-start text-[calc(21px+1vw)] uppercase *:w-full *:py-2 *:no-underline">
                  {paths.map(([key, value]) => (
                    <NavigationLink label={key} path={value} key={key} />
                  ))}
                </ul>
              </nav>
            </FocusTrap>
          ) : (
            <button
              onClick={() => setShowSidebar((value) => !value)}
              className="ms-6.5 mt-4.5 justify-self-start focus:outline-0"
              aria-label="Show navigation menu"
              type="button"
              aria-expanded={showSidebar}
            >
              <img src="/images/menu.svg" alt="Menu" height={50} width={50} />
            </button>
          )}
          <Link
            to="/"
            rel="canonical"
            className="col-start-2 mt-4 w-12.5 justify-self-center lg:hidden"
          >
            <img src="/images/logo-large.svg" alt="jorellaf Logo" width={50} />
          </Link>
        </div>
      </aside>
      <header className="pb-2.5">
        <nav className="mb-6 hidden pt-7 text-center lg:block">
          <ul className="my-4 inline-grid grid-cols-3 items-start justify-items-center gap-x-10 text-[24px] uppercase *:no-underline [&_>a]:p-2.5">
            <div>
              {paths.slice(0, Math.ceil(paths.length / 2)).map(([key, value]) => {
                if (paths.length % 2 !== 0 && key === 'Home') return null;
                return <NavigationLink label={key} path={value} key={key} />;
              })}
            </div>
            <div className="-my-5 p-2.5 ps-6 text-center">
              <Link to="/" rel="canonical">
                <img
                  src="/images/logo-large.svg"
                  alt="jorellaf Logo"
                  className="h-21"
                />
              </Link>
            </div>
            <div>
              {paths.slice(Math.ceil(paths.length / 2)).map(([key, value]) => (
                <NavigationLink label={key} path={value} key={key} />
              ))}
            </div>
          </ul>
        </nav>
      </header>
      <main aria-hidden={showSidebar}>
        <header className="text-center">
          <h1 className="mb-4 text-[calc(35px+1vw)] font-bold [font-variant:small-caps]">
            {title}
          </h1>
        </header>
        <article className="text-center [&_p]:max-w-200">
          {mainText && (
            <p className="my-5 min-w-full px-[4%] text-[calc(20px+0.5vw)] text-taupe-600">
              {mainText}
            </p>
          )}
          {mainText && (
            <hr className="mx-auto mt-12.5 mb-7.5 max-w-[85%] border border-gray-500/60" />
          )}
          {subtitle && (
            <h2 className="m-0 mb-12.5 text-[calc(26px+0.1vw)] [font-variant:small-caps]">
              {subtitle}
            </h2>
          )}

          <div className="flex min-w-full flex-wrap justify-center gap-3 pb-[calc(5%+120px)]">
            {children}
          </div>
        </article>
        <footer className="absolute bottom-0 w-full bg-black py-2.5 text-end text-xs text-white sm:text-base">
          <p className="my-4 me-6 font-bold">
            Copyright © {thisYear} jorellaf | All rights reserved
          </p>
        </footer>
      </main>
    </div>
  );
};

export default Layout;
